import {isModuleNavigationRoute} from '../../product-modules'
import {ModuleCode} from '../modules'

export type ModuleNavigationItem = {
  id: string
  titleKey?: string
  title: string
  to?: string
  icon?: string
  children?: ModuleNavigationItem[]
  source: 'permission' | 'module'
}

export type BuildModuleNavigationOptions = {
  defaultRoute: string
  menus?: string[]
  moduleCode: ModuleCode
  roleName?: string
}

type ParsedMenu = {
  group?: string
  groupIcon?: string
  icon?: string
  route: string
  titleToken: string
}

type ModuleNavigationGroup = {
  id: string
  title: string
  icon: string
}

const DEFAULT_ICON = '/media/icons/duotune/arrows/arr001.svg'

const routeStartsWith = (route: string, prefixes: string[]) =>
  prefixes.some((prefix) => route === prefix || route.startsWith(`${prefix}/`))

const assessmentGroupForRoute = (route: string): ModuleNavigationGroup | undefined => {
  if (
    routeStartsWith(route, [
      'my-course',
      'my-course-exam',
      'createexam',
      'questionbank',
      'learningobjectivequestion',
      'examquestion',
    ])
  ) {
    return {
      id: 'assessment-course-exam-workflows',
      title: 'Ders ve Sınav İşlemleri',
      icon: '/media/icons/duotune/general/gen013.svg',
    }
  }

  if (
    routeStartsWith(route, [
      'manual-exams/measurement-center',
      'exam-optical-reading',
      'studentlist',
    ])
  ) {
    return {
      id: 'assessment-application-results',
      title: 'Sınav Uygulama ve Sonuç',
      icon: '/media/icons/duotune/files/fil001.svg',
    }
  }

  if (routeStartsWith(route, ['examtype', 'questiontype'])) {
    return {
      id: 'assessment-definitions',
      title: 'Tanımlar',
      icon: '/media/icons/duotune/general/gen019.svg',
    }
  }

  return undefined
}

const moduleGroupForRoute = (
  moduleCode: ModuleCode,
  route: string
): ModuleNavigationGroup | undefined => {
  if (moduleCode === 'assessment') {
    return assessmentGroupForRoute(route)
  }

  return undefined
}

const MODULE_WORKFLOW_ROUTE_GRANTS: Partial<
  Record<ModuleCode, Array<{entry: string; grants: string[]}>>
> = {
  assessment: [
    {
      entry: 'my-course',
      grants: [
        'my-course-exam/*',
        'courseoutcomesreport',
        'my-course-outcome',
        'surveyresult',
        'createexam',
        'my-course-exam/manual',
        'manual-exams/measurement-center',
        'studentlist',
        'exam-optical-reading',
        'questionbank/*',
        'learningobjectivequestion/*',
        'my-boardmodule/*',
        'boardschedule/*',
      ],
    },
    {
      entry: 'my-course-exam',
      grants: [
        'createexam',
        'courseoutcomesreport',
        'my-course-exam/manual',
        'manual-exams/measurement-center',
        'e-exam/:examSeatAssignmentId',
        'studentlist',
        'exam-optical-reading',
      ],
    },
    {
      entry: 'question-requests/incoming',
      grants: ['learningobjectivequestion/*'],
    },
  ],
  'course-exam-schedule': [
    {
      entry: 'my-exam-schedule',
      grants: ['e-exam/:examSeatAssignmentId'],
    },
    {
      entry: 'exam-scheduler',
      grants: [
        'exam-summary',
        'department-exam-status',
        'exam-schedule-list',
        'assignment-creator',
        'faculty-availability',
        'manage-swap-requests',
        'manage-makeup-requests',
      ],
    },
    {
      entry: 'course-scheduler',
      grants: [
        'department-course-scheduler',
        'department-course-schedule',
        'university-course-scheduler',
        'university-course-schedule',
      ],
    },
  ],
  'academic-advising': [
    {
      // Canlı TEACHER menüsü legacy takip route'una gider; index route'u öğrenci
      // listesine yönlendirdiği için iki çalışma alanı tek entitlement olarak ilerler.
      entry: 'student-consultant-follow-up',
      grants: ['student-consultant-follow-up/*', 'my-consultant-students/*'],
    },
    {
      entry: 'my-consultant-students',
      grants: ['student-consultant-follow-up/*'],
    },
  ],
  curriculum: [
    {
      entry: 'my-course',
      grants: [
        'courseoutcomesreport',
        'my-course-outcome',
        'courseoutcomequestion/*',
        'learningobjectivequestion/*',
      ],
    },
  ],
  'survey-feedback': [
    {
      entry: 'survey-feedback',
      grants: ['surveyresult', 'surveyresultgeneral'],
    },
    {
      entry: 'survey',
      grants: [
        'addupdatesurvey',
        'surveyresult',
        'surveyresultmanagement',
        'surveyresultgeneral',
        'surveyresultsemester',
        'surveyresultlecturer',
      ],
    },
  ],
  committee: [
    {
      entry: 'committee',
      grants: [
        'committee-meetings/*',
        'committee-agenda/*',
        'committee-decisions/*',
        'committee-minutes/*',
      ],
    },
  ],
  quality: [
    {
      entry: 'evidence-maturity-management',
      grants: ['evidence-maturity-type/*', 'evidence-maturity-level/*'],
    },
    {
      entry: 'my-report',
      grants: ['my-reportitem/*', 'reportitemorganizationevidence/*'],
    },
    {
      entry: 'report',
      grants: [
        'reportitem/*',
        'reportitemorganization/*',
        'evidencefile/*',
        'reportitemorganizationevidence/*',
      ],
    },
  ],
}

const normalizeRole = (roleName?: string) => (roleName || '').trim().toLocaleUpperCase('tr-TR')

const isTeacherRole = (role: string) =>
  role === 'TEACHER' || role.includes('ÖĞRETİM ÜYESİ') || role.includes('ÖĞRETİM ELEMANI')

const isStudentRole = (role: string) => role === 'STUDENT' || role.includes('ÖĞRENCİ')

const splitOnce = (value: string, separator: string): [string, string] => {
  const separatorIndex = value.indexOf(separator)
  return separatorIndex < 0
    ? [value, '']
    : [value.slice(0, separatorIndex), value.slice(separatorIndex + separator.length)]
}

const splitTokenAndIcon = (value: string): [string, string | undefined] => {
  const [token, icon] = splitOnce(value, '-')
  return [token, icon || undefined]
}

const iconPath = (icon?: string) => {
  if (!icon) {
    return DEFAULT_ICON
  }

  if (icon.startsWith('/')) {
    return icon
  }

  return `/media/icons/duotune/${icon}`
}

export const parseLegacyMenuDescriptor = (descriptor: string): ParsedMenu | undefined => {
  const [scopeDefinition, itemDefinition] = splitOnce(descriptor, '_')
  if (!itemDefinition) {
    return undefined
  }

  const [scope, groupDefinition] = splitOnce(scopeDefinition, '|')
  const [routeValue, itemDefinitionValue] = splitOnce(itemDefinition, '|')
  const route = routeValue.trim().replace(/^\/+/, '')
  if (!route || !itemDefinitionValue) {
    return undefined
  }

  const [titleToken, itemIcon] = splitTokenAndIcon(itemDefinitionValue)
  const [groupToken, groupIcon] = splitTokenAndIcon(groupDefinition)

  return {
    route,
    titleToken,
    icon: itemIcon,
    group: scope === 'main' ? undefined : groupToken || scope,
    groupIcon,
  }
}

const createSyntheticItems = (
  moduleCode: ModuleCode,
  roleName?: string
): ModuleNavigationItem[] => {
  const role = normalizeRole(roleName)
  const items: ModuleNavigationItem[] = []

  if (moduleCode === 'assessment' && isTeacherRole(role)) {
    items.push({
      id: 'assessment-collaboration',
      title: 'İşbirliği',
      icon: '/media/icons/duotune/communication/com005.svg',
      children: [
        {
          id: 'assessment-question-requests',
          title: 'Tarafıma Gönderilen Soru Talepleri',
          to: '/question-requests/incoming',
          icon: '/media/icons/duotune/communication/com005.svg',
          source: 'module',
        },
      ],
      source: 'module',
    })
  }

  if (moduleCode === 'survey-feedback' && role && !isStudentRole(role)) {
    items.push({
      id: 'personal-survey-results',
      title: 'Anket Sonuçlarım',
      to: '/survey-feedback',
      icon: '/media/icons/duotune/graphs/gra006.svg',
      source: 'module',
    })
  }

  if (moduleCode === 'course-exam-schedule' && (isTeacherRole(role) || isStudentRole(role))) {
    const isTeacher = isTeacherRole(role)
    const personalItems: ModuleNavigationItem[] = [
      {
        id: 'personal-course-schedule',
        title: 'Ders Programım',
        to: isTeacher ? '/my-instructor-schedule' : '/my-student-schedule',
        icon: '/media/icons/duotune/general/gen014.svg',
        source: 'module',
      },
      {
        id: 'personal-exam-schedule',
        title: isTeacher ? 'Sınav Görevlerim' : 'Sınav Programım',
        to: '/my-exam-schedule',
        icon: '/media/icons/duotune/general/gen019.svg',
        source: 'module',
      },
    ]

    if (isTeacher) {
      personalItems.push({
        id: 'personal-exam-duty-archive',
        title: 'Görev Arşivim',
        to: '/my-exam-duty-archive',
        icon: '/media/icons/duotune/files/fil012.svg',
        source: 'module',
      })
    }

    items.push({
      id: 'personal-workspace',
      title: 'Kişisel Çalışma Alanım',
      icon: '/media/icons/duotune/communication/com013.svg',
      children: personalItems,
      source: 'module',
    })
  }

  return items
}

const hasRoute = (items: ModuleNavigationItem[], route?: string): boolean => {
  if (!route) {
    return false
  }

  return items.some(
    (item) => item.to === route || (item.children ? hasRoute(item.children, route) : false)
  )
}

export const buildModuleNavigation = ({
  defaultRoute,
  menus = [],
  moduleCode,
  roleName,
}: BuildModuleNavigationOptions): ModuleNavigationItem[] => {
  const result: ModuleNavigationItem[] = [
    {
      id: 'module-dashboard',
      title: 'Genel Bakış',
      to: defaultRoute,
      icon: '/media/icons/duotune/abstract/abs042.svg',
      source: 'module',
    },
  ]
  const groups = new Map<string, ModuleNavigationItem>()

  menus.forEach((descriptor, index) => {
    const parsed = parseLegacyMenuDescriptor(descriptor)
    if (!parsed || !isModuleNavigationRoute(moduleCode, parsed.route)) {
      return
    }

    const item: ModuleNavigationItem = {
      id: `permission-${parsed.route}-${index}`,
      titleKey: `MENU.${parsed.titleToken}`,
      title: parsed.titleToken,
      to: `/${parsed.route}`,
      icon: iconPath(parsed.icon),
      source: 'permission',
    }

    if (hasRoute(result, item.to)) {
      return
    }

    const domainGroup = moduleGroupForRoute(moduleCode, parsed.route)
    const groupKey = domainGroup?.id || parsed.group

    if (!groupKey) {
      result.push(item)
      return
    }

    let group = groups.get(groupKey)
    if (!group) {
      group = {
        id: `permission-group-${groupKey}`,
        titleKey: domainGroup ? undefined : `MENU.${parsed.group}`,
        title: domainGroup?.title || parsed.group || groupKey,
        icon: domainGroup?.icon || iconPath(parsed.groupIcon),
        children: [],
        source: 'permission',
      }
      groups.set(groupKey, group)
      result.push(group)
    }

    group.children?.push(item)
  })

  createSyntheticItems(moduleCode, roleName).forEach((item) => {
    if (item.to && hasRoute(result, item.to)) {
      return
    }

    if (item.children) {
      item.children = item.children.filter((child) => !hasRoute(result, child.to))
      if (item.children.length === 0) {
        return
      }
    }

    result.push(item)
  })

  return result
}

export const flattenModuleNavigation = (items: ModuleNavigationItem[]): ModuleNavigationItem[] =>
  items.flatMap((item) => (item.children ? flattenModuleNavigation(item.children) : [item]))

const normalizeEntitlementRoute = (route: string) =>
  route.trim().replace(/^\/+/, '').split('?')[0].split('#')[0]

const hasWorkflowEntry = (routes: Set<string>, entry: string) =>
  Array.from(routes).some((route) => route === entry || route.startsWith(`${entry}/`))

export const buildModuleRouteEntitlements = (options: BuildModuleNavigationOptions): string[] => {
  const routes = new Set(
    flattenModuleNavigation(buildModuleNavigation(options))
      .map((item) => item.to)
      .filter((route): route is string => Boolean(route))
      .map(normalizeEntitlementRoute)
  )

  MODULE_WORKFLOW_ROUTE_GRANTS[options.moduleCode]?.forEach(({entry, grants}) => {
    if (hasWorkflowEntry(routes, entry)) {
      grants.forEach((route) => routes.add(normalizeEntitlementRoute(route)))
    }
  })

  return Array.from(routes)
}

export const isModuleRouteEntitled = (routeDefinition: string, entitlements: string[]) => {
  const routeBase = normalizeEntitlementRoute(routeDefinition).replace(/\/\*$/, '')

  return entitlements.some((entitlement) => {
    const entitlementBase = normalizeEntitlementRoute(entitlement).replace(/\/\*$/, '')
    return (
      routeBase === entitlementBase ||
      routeBase.startsWith(`${entitlementBase}/`) ||
      entitlementBase.startsWith(`${routeBase}/`)
    )
  })
}
